import { apiClient } from './client';

/**
 * Cliente del módulo genérico de reportes.
 *
 * El backend expone un catálogo declarativo: el frontend no sabe qué reportes
 * existen ni qué columnas tienen, los descubre. Por eso este archivo no
 * menciona ningún reporte en concreto y `<ReportButton />` sirve para todos.
 */

export type TipoColumna = 'texto' | 'numero' | 'fecha' | 'fecha_hora' | 'booleano';

export type OperadorFiltro =
  | 'igual'
  | 'en'
  | 'contiene'
  | 'desde'
  | 'hasta'
  | 'booleano';

export interface ColumnaReporte {
  clave: string;
  titulo: string;
  tipo: TipoColumna;
  ancho?: number;
  porDefecto?: boolean;
}

export interface FiltroReporte {
  clave: string;
  columna: string;
  operador: OperadorFiltro;
  titulo: string;
  opciones?: string[];
}

export interface ResumenReporte {
  clave: string;
  titulo: string;
  descripcion: string;
  modulo: string;
}

export interface EsquemaReporte extends ResumenReporte {
  columnas: ColumnaReporte[];
  filtros: FiltroReporte[];
  ordenPorDefecto: { columna: string; direccion: 'ASC' | 'DESC' };
  maxFilas: number;
}

export type FilaReporte = Record<string, unknown>;

export interface PreviewReporte {
  columnas: ColumnaReporte[];
  filas: FilaReporte[];
  total: number;
}

/** Valores de filtro tal como los maneja la UI. */
export type ValoresFiltro = Record<string, string | string[] | boolean | undefined>;

export interface ParametrosReporte {
  columnas?: string[];
  filtros?: ValoresFiltro;
  ordenColumna?: string;
  ordenDireccion?: 'ASC' | 'DESC';
  page?: number;
  pageSize?: number;
}

type ValorQuery = string | string[] | number;

/**
 * Aplana los parámetros a la forma que espera el DTO del backend.
 *
 * Los filtros van con clave `filtros[x]` en vez de anidados como objeto porque
 * así axios los serializa de forma predecible: para un arreglo agrega `[]` al
 * final, que es justo lo que `qs` necesita para reconstruirlo del otro lado.
 */
function aParametrosQuery(params: ParametrosReporte): Record<string, ValorQuery> {
  const query: Record<string, ValorQuery> = {};

  if (params.columnas?.length) query.columnas = params.columnas.join(',');
  if (params.ordenColumna) query.ordenColumna = params.ordenColumna;
  if (params.ordenDireccion) query.ordenDireccion = params.ordenDireccion;
  if (params.page) query.page = params.page;
  if (params.pageSize) query.pageSize = params.pageSize;

  for (const [clave, valor] of Object.entries(params.filtros ?? {})) {
    // Un valor vacío significa "sin filtrar", no "igual a vacío".
    if (valor === undefined || valor === '' ) continue;
    if (Array.isArray(valor)) {
      if (valor.length === 0) continue;
      query[`filtros[${clave}]`] = valor;
    } else {
      query[`filtros[${clave}]`] = String(valor);
    }
  }

  return query;
}

/** Reportes que el rol de la sesión puede pedir. */
export async function getCatalogoReportes(): Promise<ResumenReporte[]> {
  const { data } = await apiClient.get<ResumenReporte[]>('/reportes');
  return data;
}

/** Columnas y filtros disponibles — alimenta el panel de ajuste. */
export async function getEsquemaReporte(clave: string): Promise<EsquemaReporte> {
  const { data } = await apiClient.get<EsquemaReporte>(`/reportes/${clave}/esquema`);
  return data;
}

/** Vista previa paginada. Misma consulta que la descarga, solo que acotada. */
export async function getPreviewReporte(
  clave: string,
  params: ParametrosReporte = {},
): Promise<PreviewReporte> {
  const { data } = await apiClient.get<PreviewReporte>(`/reportes/${clave}/preview`, {
    params: aParametrosQuery(params),
  });
  return data;
}

/** Lee el nombre sugerido por el servidor; si no llega, arma uno razonable. */
function nombreDesdeCabecera(cabecera: unknown, clave: string): string {
  if (typeof cabecera === 'string') {
    const coincidencia = /filename="?([^";]+)"?/i.exec(cabecera);
    if (coincidencia) return coincidencia[1];
  }
  return `${clave}.xlsx`;
}

/**
 * Descarga el .xlsx y se lo entrega al navegador.
 *
 * Va por axios y no por un `<a href>` porque un enlace plano no manda el
 * header `Authorization` y el endpoint respondería 401.
 */
export async function descargarReporte(
  clave: string,
  params: ParametrosReporte = {},
): Promise<string> {
  const respuesta = await apiClient.get<Blob>(`/reportes/${clave}/export`, {
    params: aParametrosQuery(params),
    responseType: 'blob',
    // Exportar 50.000 filas no cabe en el timeout por defecto del cliente.
    timeout: 120_000,
  });

  const nombre = nombreDesdeCabecera(
    respuesta.headers['content-disposition'],
    clave,
  );

  const url = URL.createObjectURL(respuesta.data);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = nombre;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  // Sin esto el blob queda retenido en memoria hasta recargar la página.
  URL.revokeObjectURL(url);

  return nombre;
}
